/** @jsxImportSource @emotion/react */
import { useTranslation } from "react-i18next"
import { withMyTheme } from "../../theme/theme"
import { css } from "@emotion/react"
import { mobileCss } from "../../theme/isMobile"
import { MyButton } from "../../components/button/MyButton"
import { Dialog, DialogContent, DialogTitle, Theme } from "@mui/material"
import { useState } from "react"
import { TextSizes } from "../../util/textSizes"
import { MainConfig } from "../../../mainConfig/configHelper"
import { resolveIcon } from "./menuIconResolver"
import { StayTunedPlaceholder } from "../../components/StayTunedPlaceholder"

export const MENU_ID = "menu"

const MenuContentStyle = withMyTheme((theme) => css`
    background-image: url('/images/peach_background_30.png');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;   
    height: 100vh;
    width: 100vw;
    color: ${theme.palette.text.primary};
    ${mobileCss(`
        height: auto;
        width: 100vw;
    `)}
`)

const MenuTitleStyle = withMyTheme((theme) => css`
    font-size: ${TextSizes.PAGE_TITLE};
    font-family: ${theme.typography.h1.fontFamily};
    color: ${theme.palette.primary.main};
    margin-bottom: 3rem;
    margin-top: 3rem;
    text-align: center;
    ${mobileCss(`
        font-size: clamp(2rem, 8vw, 3rem);
        margin-bottom: 2rem;
    `)}
`)

const MenuGridStyle = withMyTheme(() => css`
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 5vh;
    overflow-y: auto;
    height: 100%;
    padding: 3vh 3vh;
    ${mobileCss(`
        gap: 0.5rem;
        height: auto;
        margin-bottom: 5vh;
        overflow-y: visible;
        flex-wrap: no-wrap;
    `)}
`)

const MenuItemStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    padding: 3vh;
    max-width: 30vw;
    background: ${theme.palette.background.paper};
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    ${mobileCss(`
        max-width: 80vw;
    `)}
`)

const MenuItemHeaderStyle = withMyTheme(() => css`
    display: flex;
    align-items: center;
    gap: 1vh;
    margin-bottom: 1rem;
    ${mobileCss(`
        gap: 0.8rem;
        margin-bottom: 0.8rem;
    `)}
`)

const MenuIconStyle = withMyTheme((theme) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 5vh;
    height: 5vh;
    border-radius: 50%;
    background: ${theme.palette.secondary.light};
    color: ${theme.palette.primary.dark};
    flex-shrink: 0;
    ${mobileCss(`
        width: 40px;
        height: 40px;
    `)}
`)

const MenuIconFontSizeStyle = withMyTheme(() => css`
    font-size: 3vh;
`)

const MenuItemTitleStyle = withMyTheme((theme) => css`
    font-size: 2.75vh;
    font-weight: 700;
    color: ${theme.palette.primary.main};
    font-family: ${theme.typography.body1.fontFamily};
    ${mobileCss(`
        font-size: 1.1rem;
    `)}
`)

const MenuDescriptionStyle = withMyTheme((theme) => css`
    font-size: 1.9vh;
    color: ${theme.palette.text.primary};
    font-family: ${theme.typography.body1.fontFamily};
    line-height: 1.5;
    margin-bottom: 1rem;
    white-space: pre-line;
    ${mobileCss(`
        font-size: 0.9rem;
        margin-bottom: 0.8rem;
    `)}
`)

const MenuButtonStyle = withMyTheme(() => css`
    padding: .5vh 1.2vh;
    border-radius: 5vh;
    margin-top: 1vh;
    align-self: flex-start;
    ${mobileCss(`
        font-size: 0.85rem;
        padding: 0.4rem 0.8rem;
    `)}
`)

const MenuDialogContentStyle = withMyTheme(() => css`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    align-content: center;
    justify-content: center;
    justify-items: center;
`)

const MenuDialogTitleStyle = withMyTheme((theme: Theme) => css`
    font-family: ${theme.typography.h1.fontFamily};
    font-weight: ${theme.typography.h1.fontWeight};
    font-size: 4.5vh;
    text-align: center;
    margin-top: 1em;
`)

const MenuDialogDescriptionStyle = withMyTheme((theme: Theme) => css`
    font-size: 2vh;
    color: black;
    text-align: center;
    white-space: pre-line;
    font-family: ${theme.typography.body1.fontFamily};
    font-weight: 300;

    ${mobileCss(`
        font-size: 4vw;
    `)}
`)

const MenuDialogButtonWrapperStyle = withMyTheme(() => css`
    margin-top: 3vh;
    margin-bottom: 2vh;
`)

interface MenuItemComponentProps {
    title: string;
    description?: string;
    icon: React.ReactNode;
    showButton?: boolean;
    onButtonClick?: () => void;
}

const MenuItemComponent = ({ title, description, icon, showButton = true, onButtonClick }: MenuItemComponentProps) => {
    const { t } = useTranslation()

    return <div css={MenuItemStyle}>
        <div css={MenuItemHeaderStyle}>
            <div css={MenuIconStyle}>
                {icon}
            </div>
            <div css={MenuItemTitleStyle}>{title}</div>
        </div>
        {description && (
            <div css={MenuDescriptionStyle} dangerouslySetInnerHTML={{ __html: description }} />
        )}
        {showButton && (
            <MyButton
                text={t('menu.seeDetails')}
                variant="outlined"
                additionalCss={MenuButtonStyle}
                enabled={true}
                onClick={onButtonClick}
            />
        )}
    </div>
}

export const Menu = () => {
    const { t } = useTranslation()
    const [selectedMenuItem, setSelectedMenuItem] = useState<{ title: string; description: string } | null>(null)
    const [modalOpen, setModalOpen] = useState(false)

    const sections = MainConfig.menu?.sections ?? []
    const isStayTuned = MainConfig.menu?.stayTuned === true

    const handleOpenModal = (title: string, details: string) => {
        setSelectedMenuItem({ title, description: details })
        setModalOpen(true)
    }

    const handleCloseModal = () => {
        setModalOpen(false)
    }

    const MENU_EMOJIS = ['🍽️', '🥂', '🍰', '👨‍🍳', '✨']

    return (
        <section id={MENU_ID} css={MenuContentStyle}>
            <div css={MenuTitleStyle}>
                {t('menu.title')}
            </div>

            {isStayTuned ? (
                <StayTunedPlaceholder
                    message={t('menu.stayTunedMessage')}
                    subtext={t('menu.stayTunedSubtext')}
                    emojis={MENU_EMOJIS}
                />
            ) : (
                <>
                    <div css={MenuGridStyle}>
                        {sections.map((section) => {
                            const icon = resolveIcon(section.iconType, section.iconValue, MenuIconFontSizeStyle)
                            const showInline = !section.expandInDialog

                            return (
                                <MenuItemComponent
                                    key={section.id}
                                    title={section.title}
                                    description={showInline ? section.details : undefined}
                                    icon={icon}
                                    showButton={section.expandInDialog}
                                    onButtonClick={() => handleOpenModal(section.title, section.details)}
                                />
                            )
                        })}
                    </div>

                    <Dialog onClose={handleCloseModal} open={modalOpen}>
                        <DialogTitle>
                            <div css={MenuDialogTitleStyle}>
                                {selectedMenuItem?.title}
                            </div>
                        </DialogTitle>
                        <DialogContent>
                            <div css={MenuDialogContentStyle}>
                                {!!selectedMenuItem && <div css={MenuDialogDescriptionStyle} dangerouslySetInnerHTML={{ __html: selectedMenuItem?.description }}/> }
                                <div css={MenuDialogButtonWrapperStyle}>
                                    <MyButton
                                        variant="contained"
                                        colorVariant="primary"
                                        text={t('location.close')}
                                        onClick={handleCloseModal}
                                    />
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>
                </>
            )}
        </section>
    )
}
